import { PinButton, pinButtonStyles } from '@components/PinButton/PinButton';
import { formatLibraryCardMeta } from '@lib/utils/libraryStats';
import { LibraryStore } from '@stores/Library/Library';
import { libraryFavorites } from '@stores/LibraryFavorites';
import { observer } from 'mobx-react';
import { RefObject, useCallback, useMemo } from 'react';
import { LibraryCard } from './LibraryCard';
import { LibraryDeleteModalRef } from './LibraryDeleteModal';
import { LibraryRenameModalRef } from './LibraryRenameModal';

interface LibraryGridCardProps {
  library: LibraryStore;
  isHidden: boolean;
  /** Desktop only — the reveal-on-hover pin is unreachable by touch. */
  showPin: boolean;
  onLibrarySelect: (libraryId: string, libraryName: string) => void;
  onMobileMenuClick?: (libraryId: string, libraryName: string, thumbnailBlobUrl?: string) => void;
  onAddMedia: (libraryId: string) => void;
  renameModalRef: RefObject<LibraryRenameModalRef | null>;
  deleteModalRef: RefObject<LibraryDeleteModalRef | null>;
}

/**
 * One cell of the library grid: the card, its pin, and the per-library closures that bind
 * the grid's page-level handlers to this library.
 *
 * It exists to be a **memo boundary in front of `LibraryCard`**. `observer()` wraps
 * components in `React.memo`, so `LibraryCard` is able to skip re-rendering when something
 * unrelated changes — but it never did, because building those three closures inside the
 * grid's `map` handed it fresh function props on every render. Owning them here, behind
 * `useCallback`, means every prop this passes down is referentially stable.
 *
 * What that buys, precisely:
 *
 *  - **Pin toggle.** This wrapper re-renders for *every* library, because
 *    `libraryFavorites.isFavorited` reads one shared `favoriteIdSet` computed that any pin
 *    change invalidates. But each wrapper then hands `LibraryCard` identical props, so the
 *    expensive subtree (cover resolution, verb rail, `LibraryCardView`) re-renders nowhere
 *    except where something actually changed. Only this body — four strings, four hooks, a
 *    div and a `PinButton` — re-runs. That is deliberate: per-key pin granularity would
 *    mean maintaining a parallel `observable.map` beside the ordered id array, and the
 *    invariant costs more than the render it saves.
 *  - **Stats streaming in.** The `library.*` reads that used to sit in the grid's `map` sit
 *    here now, so one library's stats arriving re-renders one wrapper instead of all of
 *    them.
 *
 * Every prop **passed to `LibraryCard`** is deliberately a primitive, a stable store reference or
 * a stable callback. Adding one that changes identity per render silently undoes all of the above
 * — it is not a visible break, just a quiet return to re-rendering every card. (The `PinButton`
 * below is a plain function component, so its handler needs no such care.)
 *
 * The root element must stay the `cardWrapper` div: `.cardWrapper:hover` (and its keyboard
 * counterpart, `.cardWrapper:has(:focus-visible)`) is what reveals the pin, and the grid
 * container lays out its direct children.
 */
export const LibraryGridCard = observer(function LibraryGridCard({
  library,
  isHidden,
  showPin,
  onLibrarySelect,
  onMobileMenuClick,
  onAddMedia,
  renameModalRef,
  deleteModalRef,
}: LibraryGridCardProps) {
  const libraryId = library.libraryId;
  const name = library.name || '';
  // The two lines under the card's name. Destructured into primitives here rather than passed
  // as the object: every prop `LibraryCard` receives has to keep a stable identity or the memo
  // below stops skipping siblings. See this component's docstring.
  const { mediaCount, folderCount, owner } = formatLibraryCardMeta(library);

  const handleSelect = useCallback(
    () => onLibrarySelect(libraryId, name),
    [onLibrarySelect, libraryId, name],
  );

  const handleAddMedia = useCallback(() => onAddMedia(libraryId), [onAddMedia, libraryId]);

  // Presence is meaningful, not just the behaviour: the card renders its ⋯ button only when
  // it has a handler, so this has to stay `undefined` on desktop rather than become a no-op.
  const handleMobileMenuClick = useMemo(
    () =>
      onMobileMenuClick
        ? (thumbnailBlobUrl?: string) => onMobileMenuClick(libraryId, name, thumbnailBlobUrl)
        : undefined,
    [onMobileMenuClick, libraryId, name],
  );

  return (
    <div className={pinButtonStyles.cardWrapper}>
      <LibraryCard
        library={library}
        id={libraryId}
        name={name}
        mediaCount={mediaCount ?? undefined}
        folderCount={folderCount}
        ownerLine={owner ?? undefined}
        onSelect={handleSelect}
        thumbnailMediaId={library.data?.userMetadata?.thumbnailMediaId}
        isWelcomeLibrary={library.isDemo}
        isHidden={isHidden}
        renameModalRef={renameModalRef}
        deleteModalRef={deleteModalRef}
        onMobileMenuClick={handleMobileMenuClick}
        onAddMedia={handleAddMedia}
      />
      {/* Inline handler, unlike the three above: `PinButton` is a plain function component, so a
          stable identity here couldn't skip anything. */}
      {showPin && (
        <PinButton
          isFavorited={libraryFavorites.isFavorited(libraryId)}
          onToggle={() => libraryFavorites.toggleFavorite(libraryId, 'card')}
        />
      )}
    </div>
  );
});
